import { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Tab from 'react-bootstrap/Tab';
import Image from 'react-bootstrap/Image';
import Nav from 'react-bootstrap/Nav';
import Button from 'react-bootstrap/Button';
import DynamicDatePicker from '../Components/DynamicDatePicker';
import { parse } from 'date-fns';
import PreviousIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import NextIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import InsertIcon from '@mui/icons-material/AddBoxOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import CrudList from './CrudList';
import AutoComplete from './AutoComplete';
import { callApi, getPropValue, setPropValue, ErrorAlert, Round, SuccessAlert } from './utils';
import useApp from './../AppContext';
import './CrudForm.css';
import { t } from "./../i18n"
import { getCultureInfo } from "./utils";
import CustomDropdown from '../Components/CustomDropdown';

function CrudForm(props) {
    const app = useApp();
    const [language] = useState(getCultureInfo(app.sessionToken?.Language));
    const [error, setError] = useState(null);
    const [serverUrl] = useState(app.serverUrl);
    const [layout, setLayout] = useState(null);
    const [allCols, setAllCols] = useState(null);
    const [lookUpInfo, setLookUpInfo] = useState(null);
    const [obj, setObj] = useState({});
    const [listPrmVals, setListPrmVals] = useState([]);
    const [disabledCols, setDisabledCols] = useState({});
    const [designMode, setDesignMode] = useState(false);
    const [newColPos, setNewColPos] = useState(null);
    const [loadObj, setLoadObj] = useState(false);

    const IconStylesPrevNext = {
        fontSize: '20px',
        color: app.theme === 'dark' ? 'var(--text-secondary)' : '#353839'
    };
    const IconStyles = {
        fontSize: '22px',
        color: app.theme === 'dark' ? 'var(--text-secondary)' : '#353839'
    };
    const [successMessage, setSuccessMessage] = useState(false);

    useEffect(() => {
        setLayout(props.layout);
        if (!props.layout || !props.layout.tabs) return;
        if (props.layout.queryObject === props.dataContext.getLayout()) {
            setLookUpInfo(null);
        }
        setAllCols(props.layout.tabs.map(tab => tab.columns).reduce((prev, current) => prev ? prev.concat(current) : current));
    }, [props.layout, props.dataContext]);

    useEffect(() => {
        if (props.objUnderEdit && props.objUnderEdit.current) {
            setObj(JSON.parse(JSON.stringify(props.objUnderEdit.current)));
            let allcoldisable = Object.fromEntries(
                allCols.map((col) => [col.columnName, true])
            );
            if (props.allowUpdate === false) {
                setDisabledCols(allcoldisable);
            }
            else if (props.objUnderEdit?.rowRestrictions?.canBeUpdated === false) {
                setDisabledCols(allcoldisable);
            }
            else if (!props.objUnderEdit.disabledFields) {
                setDisabledCols({});
            } else {
                setDisabledCols(props.objUnderEdit.disabledFields);
            }
            setLookUpInfo(null);
            setLoadObj(true);
        }
    }, [props.objUnderEdit, allCols, props.allowUpdate]);

    useEffect(() => {
        async function getLookUpInfo() {
            if (!props.active || !allCols || lookUpInfo) return;
            let foreignCols = allCols.filter(col => col.controlType === "DropDownLookUpList");
            let tmpLookUpInfo = {}, tmpForeignKeyFields = [];
            try {
                for (let i = 0; i < foreignCols.length; i++) {
                    const gqlResult = await callApi('POST', `${serverUrl}/api/query`, props.userToken,
                        { query: foreignCols[i].lookUpQuery, vars: {} });
                    tmpLookUpInfo[foreignCols[i].columnName] = {
                        "values": gqlResult.data[Object.keys(gqlResult.data)[0]],
                        "keyFields": foreignCols[i].keyFields
                    };
                    tmpForeignKeyFields = tmpForeignKeyFields.concat(foreignCols[i].keyFields.map(k => k.foreignField));
                }
                setLookUpInfo(tmpLookUpInfo);
            } catch (err) {
                setError(err.message);
            }
        }
        getLookUpInfo();
    }, [allCols, lookUpInfo, props.active, props.userToken, serverUrl]);
    /*
    function validateTime(s, col, throwError) {
        if (s.length !== 5 ||
            s.substr(2, 1) !== ":" ||
            isNaN(s.substr(0, 2)) ||
            isNaN(s.substr(3, 2)) ||
            parseInt(s.substr(0, 2)) < 0 || parseInt(s.substr(0, 2)) > '23' ||
            parseInt(s.substr(3, 2)) < 0 || parseInt(s.substr(3, 2)) > '59'
        ) {
            if (throwError) {
                throw new Error(`${s} is not a valid time for field ${col.caption}`);
            } else {
                return false;
            }
        }
        return true;
    }

    function validateDate(s, col, throwError) {
        let isValid = isDateValid(s);
        if (!isValid) {
            if (throwError) {
                throw new Error(`${s} is not a valid date for field ${col.caption}`);
            } else {
                return false;
            }
        }
        return true;
    }
    */

    function checkRequiredFields() {
        if (!allCols) return true;
        if (!obj) return true;
        if (props.allowUpdate === false) return true;
        if (props.objUnderEdit?.rowRestrictions?.canBeUpdated === false) return true;

        let reqFields = allCols.filter(c => c.isRequired === true);
        for (let i = 0; i < reqFields.length; i++) {
            let colValue = getPropValue(obj, reqFields[i].columnName);
            if (colValue === null || colValue === undefined || colValue === "") {
                setError(`${t('common.requiredField', language)} ${reqFields[i].caption}`);
                return false;
            }
        }
    }

    async function saveClicked() {
        try {
            if (checkRequiredFields() === false) return;
            setSuccessMessage(t('common.changesSaved', language));
            setTimeout(() => {
                setSuccessMessage(null);
            }, 2500);
            await props.dataContext.endEdit(layout.queryObject, true);
        } catch (err) {
            setError(err.message);
        }
    }

    async function undoClicked() {
        try {
            await props.dataContext.undo(layout.queryObject);
        } catch (err) {
            setError(err.message);
        }
    }

    async function setObjectValue(column, value, selectedIndex) {
        let tmpObj = { ...obj };
        setPropValue(tmpObj, column.columnName, value);
        if (selectedIndex >= 0 && lookUpInfo[column.columnName] && lookUpInfo[column.columnName].keyFields) {
            for (let i = 0; i < lookUpInfo[column.columnName].keyFields.length; i++) {
                let kf = lookUpInfo[column.columnName].keyFields[i];
                if (selectedIndex === 0) tmpObj[kf.foreignField] = null;
                else tmpObj[kf.foreignField] = lookUpInfo[column.columnName].values[selectedIndex - 1][kf.primaryField];
                await onColumnChanged(kf.foreignField, tmpObj[kf.foreignField], tmpObj);
            };
        }
        setObj(tmpObj);
        if (props.onChange) {
            props.onChange(tmpObj);
        }
        if (column.controlType === "CheckBox" || column.controlType === "DropDownEnumList" || column.controlType === "DropDownValueList" || column.controlType === "AutoCompleteLookUp" || column.format === "dt" || column.format === "d" || column.format === "t") {
            await onColumnChanged(column.columnName, value, tmpObj);
        }
    }

    function formatDateValue(colValue, format) {
        if (!colValue) return colValue;
        let retVal = colValue;
        let d = parse(colValue, "dd/MM/yyyy HH:mm", new Date())
        if (!(d instanceof Date) || isNaN(d)) {
            if (retVal.length >= 13) {
                d = new Date(retVal);
            }
            else {
                d = parse(colValue,
                    (format === 'dt') ? "dd/MM/yyyy HH:mm" : (format === 't') ? "HH:mm" : "dd/MM/yyyy",
                    new Date());
            }
        }
        if ((d instanceof Date)) {
            if (format === 'd') {
                return d.getDate().toString().padStart(2, '0') + "/" +
                    (d.getMonth() + 1).toString().padStart(2, '0') + "/" +
                    d.getFullYear();
            }
            else if (format === 't') {
                return d.getHours().toString().padStart(2, '0') + ":" +
                    d.getMinutes().toString().padStart(2, '0');
            }
            else if (format === 'dt') {
                return d.getDate().toString().padStart(2, '0') + "/" +
                    (d.getMonth() + 1).toString().padStart(2, '0') + "/" +
                    d.getFullYear() + " " +
                    d.getHours().toString().padStart(2, '0') + ":" +
                    d.getMinutes().toString().padStart(2, '0');
            }
        }
        return retVal;
    }

    async function onColumnChanged(columnName, newValue, xObj) {
        if (!props.dataContext) return;
        await props.dataContext.onColumnChanged(layout.queryObject, columnName, newValue);
    }

    async function fieldLostFocus(column, newValue) {
        onColumnChanged(column.columnName, newValue);
    }

    async function actionOkClicked() {
        try {
            let tmpObj = { ...obj };
            if (!props.onActionOk) return;
            props.onActionOk(props.action, tmpObj);
        } catch (err) {
            setError(err.message);
        }
    }

    function Getdisabled(col) {
        return disabledCols[col] ? disabledCols[col] : disabledCols[col.replaceAll(".", "_")];
        /// return  disabledCols[col.replaceAll(".", "_")];
    }

    function createTextBox(column) {
        let colValue = getPropValue(obj, column.columnName)
        // if (column.format === 'd' || column.format === 't' || column.format === 'dt') {
        //     colValue = formatDateValue(colValue, column.format);
        // }
        return <Form.Control
            /*placeholder={column.caption}*/
            type={column.isPassword ? 'password' : 'text'}
            value={colValue}
            onChange={e => setObjectValue(column, e.target.value)}
            readOnly={column.editNotAllowed === true}
            disabled={Getdisabled(column.columnName)}
            onBlur={e => fieldLostFocus(column, e.target.value)}
            required={column.isRequired === true}
        />

    }

    function crateNumericTextBox(column) {
        let colValue = getPropValue(obj, column.columnName)
        let decimals = Number(column.decimals || 0);
        if (decimals > 0) {
            colValue = Round(colValue, decimals);
        }
        return <Form.Control
            value={isNaN(colValue) ? '' : colValue}
            type="number"
            onChange={e => setObjectValue(column, e.target.value)}
            readOnly={column.editNotAllowed === true}
            disabled={Getdisabled(column.columnName)}
            onBlur={e => fieldLostFocus(column, e.target.value)}
        />;
    }
    function CreatDatetime(column) {
        let pickerType = (column.format === 'dt') ? "DateTime" : (column.format === 't') ? "Time" : "Date";
        let format = (column.format === 'dt') ? "dd/MM/yyyy HH:mm" : (column.format === 't') ? "HH:mm" : "dd/MM/yyyy";
        return <DynamicDatePicker
            caption=""
            pickerType={pickerType}
            dateFormat={format}
            value={formatDateValue(getPropValue(obj, column.columnName), column.format)}
            onChange={(value) => setObjectValue(column, value)}
            readOnly={column.editNotAllowed === true}
            disabled={Getdisabled(column.columnName)}
        />;
    }
    function createCheckBox(column) {
        return <Form.Check
            type="checkbox"
            checked={getPropValue(obj, column.columnName)}
            id={column.fieldName}
            onChange={e => setObjectValue(column, e.target.checked)}
            disabled={Getdisabled(column.columnName)}
            label={column.caption}
        />;
    }

    function createDropDownValueList(column) {
        const options = column.controlOptions.map(val => ({
            value: val,
            label: val
        }));

        return (
            <CustomDropdown
                options={options}
                value={getPropValue(obj, column.columnName)}
                onChange={(e) => setObjectValue(column, e.target.value)}
                readonly={column.editNotAllowed === true}
                disabled={Getdisabled(column.columnName)}
                showEmptyOption={false}
                placeholder="Επιλέξτε..."
            />
        );
    }

    function createDropDownEnumList(column, dataType) {
        const options = column.controlOptions.map(enm => ({
            value: dataType === 'Double' ? Number(enm.value) : enm.value,
            label: enm.text
        }));

        return (
            <CustomDropdown
                options={options}
                value={getPropValue(obj, column.columnName)}
                onChange={(e) => setObjectValue(column, dataType === 'Double' ? Number(e.target.value) : e.target.value)}
                readonly={column.editNotAllowed === true}
                disabled={Getdisabled(column.columnName)}
                showEmptyOption={false}
                placeholder="Επιλέξτε..."
            />
        );
    }

    function createDropDownLookUpList(column) {
        if (!lookUpInfo || !lookUpInfo[column.columnName]) return null;
        
        const options = lookUpInfo[column.columnName].values.map(lkpObj => ({
            value: lkpObj[column.dropDownDisplay],
            label: lkpObj[column.dropDownDisplay]
        }));

        return (
            <CustomDropdown
                options={options}
                value={getPropValue(obj, column.columnName)}
                onChange={(e) => setObjectValue(column, e.target.value, e.target.selectedIndex)}
                readonly={column.editNotAllowed === true}
                disabled={Getdisabled(column.columnName)}
                showEmptyOption={true}
                placeholder="Επιλέξτε..."
            />
        );
    }

    function createAutoComplete(column) {
        return <AutoComplete
            as="AutoComplete"
            placeholder=""
            serverUrl={serverUrl}
            props={props}
            col={column}
            obj={obj}
            disabled={Getdisabled(column.columnName)}
            onChange={e => setObjectValue(column, e ? e.option.lkpField : "")}
        />;
    }

    function createImageUrlControl(column) {
        let imageUrlTextBox = createTextBox(column);
        let src = getPropValue(obj, column.columnName);
        if (column.path === "relative") {
            src = serverUrl + "/" + src;
        }
        return (
            <>
                {imageUrlTextBox}
                <Col xs={6} className="mt-2">
                    <a target="_blank" rel="noopener noreferrer" href={src}><Image src={src} rounded fluid /></a>
                </Col>
            </>
        );
    }

    function createTextArea(column) {
        const colValue = getPropValue(obj, column.columnName);
        const lines = colValue ? colValue.split('\n').length : 1;
        const calculatedRows = Math.max(5, Math.min(lines + 1, 30));

        return <Form.Control
            as="textarea"
            rows={calculatedRows}
            value={colValue}
            onChange={e => setObjectValue(column, e.target.value)}
            readOnly={column.editNotAllowed === true}
            onBlur={e => fieldLostFocus(column, e.target.value)}
            disabled={Getdisabled(column.columnName)}
        />;
    }

    function createList(column) {
        let prmValues = {};
        if (listPrmVals.find(l => l.column === column)) {
            prmValues = listPrmVals.find(l => l.column === column).prmValues;
        } else {
            column.queryParameters.forEach(r => {
                if (!getPropValue(obj, r.objectField)) return;
                prmValues[r.parameterField] = getPropValue(obj, r.objectField)
            });
            if (Object.keys(prmValues).length === 0) return null;
            let tmpListPrmVals = [...listPrmVals];
            tmpListPrmVals.push({
                column: column,
                prmValues: prmValues
            });
            setListPrmVals(tmpListPrmVals);
        }
        return <CrudList layoutUrl={column.layoutUrl} userToken={props.userToken} prmValues={prmValues} />
    }

    function createControl(column) {
        if (column.controlType === "CheckBox") {
            return createCheckBox(column);
        } else if (column.controlType === "DropDownValueList") {
            return createDropDownValueList(column);
        } else if (column.controlType === "DropDownLookUpList") {
            return createDropDownLookUpList(column);
        } else if (column.controlType === "ImageUrl") {
            return createImageUrlControl(column);
        } else if (column.controlType === "TextArea") {
            return createTextArea(column);
        } else if (column.controlType === "DropDownEnumList") {
            return createDropDownEnumList(column, column.dataType);
        } else if (column.controlType === "List") {
            return createList(column);
        } else if (column.controlType === "AutoCompleteLookUp") {
            return createAutoComplete(column);
        }
        else if (column.controlType === 'TextBox' && column.dataType === 'Double') {
            return crateNumericTextBox(column);
        }
        else if (column.format === 'd' || column.format === 't' || column.format === 'dt') {
            return CreatDatetime(column);
        } else {
            return createTextBox(column);
        }
    }

    function deleteCol(tabIndex, colIndex) {
        setLayout(prev => {
            prev = JSON.parse(JSON.stringify(prev));
            if (newColPos) {
                prev.tabs[newColPos.tabIndex].columns.splice(newColPos.colIndex + 1, 0, prev.tabs[tabIndex].columns[colIndex]);
            }
            prev.tabs[tabIndex].columns.splice(colIndex, 1);
            setNewColPos(null);
            return prev;
        });
    }

    function keepNewColPos(tabIndex, colIndex) {
        setNewColPos({ tabIndex, colIndex });
    }

    function createTabs(obj) {
        if (!props.layoutUrl && props.isActionRequestForm) {
            return <p>Would you like to continue?</p>;
        }
        if (!layout || !layout.tabs) return null;
        if (!loadObj) return null;
        let tabElements = layout.tabs.map((tab, i) => {
            let fieldElements = tab.columns.map((column, j) => {
                if (column.controlType === "List") return createControl(column);
                let colWidth = 12;
                if (designMode) {
                    colWidth = 10;
                }
                if (column.secondaryField === null && column.controlType !== "AutoCompleteLookUp") {
                    return <Form.Group key={i + "." + j} as={Row} className="label-and-control">
                        {column.controlType !== "CheckBox" ? <Form.Label className="label">
                            {column.caption}
                            {column.isRequired === true && <span className="required-bullet"> *</span>}
                        </Form.Label> : null}
                        <Col sm={colWidth}>{createControl(column)}</Col>
                        {designMode ? <Col sm={1}><Button className="ms-1" size="sm" variant="" onClick={() => { keepNewColPos(i, j) }}><InsertIcon /></Button></Col> : null}
                        {designMode ? <Col sm={1}><Button className="ms-1" size="sm" variant="" onClick={() => { deleteCol(i, j) }}><DeleteIcon /></Button></Col> : null}
                    </Form.Group>
                } else {
                    return <Form.Group key={i + "." + j} as={Row} className="label-and-control">
                        <Col sm={colWidth}>{createControl(column)}</Col>
                        {designMode ? <Col sm={1}><Button className="ms-1" size="sm" variant="" onClick={() => { keepNewColPos(i, j) }}><InsertIcon /></Button></Col> : null}
                        {designMode ? <Col sm={1}><Button className="ms-1" size="sm" variant="" onClick={() => { deleteCol(i, j) }}><DeleteIcon /></Button></Col> : null}
                    </Form.Group>
                }
            });
            return (
                <Tab.Pane className="pt-2" key={i} eventKey={"tab" + i}>
                    {fieldElements}
                </Tab.Pane>
            );
        });
        return <Tab.Container id="left-tabs-example" defaultActiveKey="tab0">
            <div className="tabs-wrapper">
                <div className="tabs-scroll">
                    <Nav as='ul' variant="tabs">
                        {layout.tabs.map((tab, i) => {
                            return <Nav.Item key={i} as='li'>
                                <Nav.Link as='button' eventKey={"tab" + i}>{tab.caption}</Nav.Link>
                            </Nav.Item>
                        })}
                    </Nav>
                </div>
            </div>
            <Tab.Content className="custom-tabs" >{tabElements}</Tab.Content>
        </Tab.Container>
    }

    function hide() {
        if (props.onHide) props.onHide();
    }
    function getCurrentIndex() {
        if (!props.dataContext || !layout?.queryObject || !props.objUnderEdit?.current) return -1;
        const allData = props.dataContext.getLoadedData(layout.queryObject);
        if (!allData || allData.length === 0) return -1;
        const current = props.objUnderEdit.current;
        const exactIndex = allData.findIndex(r => r === current);
        if (exactIndex !== -1) return exactIndex;
        for (const key of layout?.primaryKey) {
            if (current[key] !== undefined && current[key] !== null) {
                const idx = allData.findIndex(r => r[key] === current[key]);
                if (idx !== -1) return idx;
            }
        }
        return allData.findIndex(record => {
            for (const key in current) {
                if (current[key] !== record[key]) {
                    return false;
                }
            }
            return true;
        });
    }

    function createBody() {
        if (!layout?.queryObject) {
            return <div className="loading">Loading...</div>;
        }
        const queryObject = layout.queryObject;
        const allData = props.dataContext?.getLoadedData(queryObject) || [];
        const totalRecords = allData.length;
        const currentIdx = getCurrentIndex();
        const canMovePrevious = currentIdx === 0;
        const canMoveNext = currentIdx === totalRecords - 1 || currentIdx === -1;
        // console.log("currentIdx", currentIdx, "totalRecords", totalRecords);

        return <>
            <ErrorAlert message={error} setMessage={setError} language={language} />
            <SuccessAlert message={successMessage} setMessage={setSuccessMessage} language={language}/>
            {!props.isActionRequestForm ? <div className="top-buttons">
                {!props.isStandAlone ? <>
                    <button className="btn" onClick={() => { props.dataContext.moveCurrentEdit(layout.queryObject, -1); }} disabled={canMovePrevious}><PreviousIcon sx={IconStylesPrevNext} /></button>
                    <button className="btn" onClick={() => { props.dataContext.moveCurrentEdit(layout.queryObject, 1); }} disabled={canMoveNext}><NextIcon sx={IconStylesPrevNext} /></button>
                </> : null
                }
                <>
                    {props.allowUpdate === true && (
                        <button className="btn" onClick={saveClicked}><SaveIcon sx={IconStyles}></SaveIcon></button>
                    )}
                    <button className="btn" onClick={undoClicked}><UndoIcon sx={IconStyles}></UndoIcon></button>
                    <button className="btn ms-auto" onClick={() => setDesignMode(!designMode)}><SettingsIcon sx={IconStyles} /></button>
                    {newColPos ? <span>Move Column Mode</span> : null}
                </>
            </div> : null}
            {(layout && layout.queryObject) ? createTabs(obj) : null}
        </>;
    }

    function createStandAlone() {
        return createBody();
    }

    function createModal() {
        return (
            <Modal show={props.active} onHide={hide} backdrop="static" size={(layout && layout.size) ? layout.size : "sm"}>
                <Modal.Header closeButton className='header-crud-form'>
                    <Modal.Title>{layout ? layout.caption : null}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='modal-crud-body'>
                    {createBody()}
                </Modal.Body>
                {props.isActionRequestForm ? <Modal.Footer className="justify-content-left">
                    <Button variant="primary" onClick={actionOkClicked}>OK</Button>
                </Modal.Footer > : null}

            </Modal >
        );
    }

    if (props.isStandAlone) {
        return createStandAlone();
    } else {
        return createModal();
    }
}

export default CrudForm;